import { useMemo, useState } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { Icon, Select, SelectDropdownOption, Badge } from '../../..'
import { mtc } from '../../../utils/style-helpers'

export interface MultiselectProps<T extends string> {
  options: SelectDropdownOption<T>[]
  value: T[]
  onChange: (value: T[]) => void
  disabled?: boolean
  onBlur?: () => void
  placeholder?: string
}

export const Multiselect = <T extends string>({
  value,
  options,
  onChange,
  disabled,
  onBlur,
  placeholder,
}: MultiselectProps<T>) => {
  const [searchValue, setSearchValue] = useState('')
  // Select has trouble with its open/closed state management when making selections so it must be manually handled
  const [open, setOpen] = useState(false)

  const shownOptions = useMemo(
    () =>
      options.filter((option) =>
        String(option.text).toLowerCase().includes(searchValue.toLowerCase())
      ),
    [options, searchValue]
  )

  return (
    <Select open={open} disabled={disabled} onBlur={onBlur}>
      <Select.Trigger
        onClick={() => setOpen((prev) => !prev)}
        className="multi-select-trigger min-h-[48px] h-auto"
      >
        <Icon icon={regular('search')} />
        <div className={mtc('flex flex-1 ml-2 flex-wrap gap-y-2')}>
          {value.map((v) => (
            <Badge
              key={v}
              color="olive"
              variant="secondary"
              className="mr-2 cursor-default"
              onClick={(e) => e.stopPropagation()}
            >
              {options.find((option) => option.value === v)?.text}
              <Icon
                icon={regular('xmark')}
                onClick={() => onChange(value.filter((val) => val !== v))}
                color="text-moss-200"
                className="ml-3 cursor-pointer"
              />
            </Badge>
          ))}

          <div
            role="presentation"
            onClick={(e) => {
              // Prevent parent from getting on click
              e.stopPropagation()
            }}
          >
            <input
              placeholder={placeholder || 'Search'}
              className={mtc(
                'w-[100px] h-[30px] outline-none text-neutral-600'
              )}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value)
                setOpen(true)
              }}
              onBlur={(e) => {
                // The select parent is attempting to eat all of the events on the child, not sure how or why they are
                // bubbling up but it has something to do with radix's implementation of select.  This is a hack to
                // prevent that and keep the focus on the search bar
                if (
                  e.relatedTarget?.classList.contains('multi-select-content') ||
                  e.relatedTarget?.classList.contains(
                    'multi-select-content-item'
                  ) ||
                  e.relatedTarget?.classList.contains('multi-select-trigger')
                ) {
                  e.currentTarget.focus()
                } else {
                  // Clear search if user clicks away
                  setSearchValue('')
                }
              }}
              onClick={(e) => {
                e.currentTarget.focus()
                setOpen(true)
              }}
            />
          </div>
        </div>
      </Select.Trigger>

      <Select.Content
        onPointerDownOutside={() => setOpen(false)}
        className="multi-select-content"
      >
        {shownOptions.map(({ key, value: optionValue, text, onClick }) => (
          <Select.Item
            className="multi-select-content-item"
            value={optionValue ?? ''}
            key={key || optionValue}
            onClick={() => {
              if (optionValue !== undefined) {
                if (value.includes(optionValue)) {
                  onChange(value.filter((val) => val !== optionValue))
                } else {
                  onChange([...value, optionValue])
                }
              }

              // Clear search value
              setSearchValue('')

              onClick?.()
            }}
            data-state={
              optionValue !== undefined && value.includes(optionValue)
                ? 'checked'
                : 'unchecked'
            }
          >
            {text}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}
